/**
 * Connected / Disconnected / Valve isolation trace.
 *
 * Click a pipe; the panel walks outward through everything joined to it and
 * reports how far the network runs, which layers it crosses, and what sits on
 * it (Connected) — or, flipped, everything in the chosen layers that walk did
 * NOT reach (Disconnected, Esri's own trace type of the same name). The
 * tracing itself is in utils/networkTrace — including a plain account of what
 * a geometric trace can and cannot know.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, FileSpreadsheet, FileText, Loader2, MapPin, Share2, ShieldAlert, Unlink } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import {
  EmptyHint,
  Field,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
} from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../../utils/arcgis';
import { featureKey } from '../../../utils/featureKey';
import { fetchLayerFieldNames } from '../../../tools/toolFields';
import { downloadCsv, toCsv } from '../../../utils/csv';
import { printSheet } from '../../../utils/printSheet';
import { renderIsolationReportSheet, type IsolationReportData } from '../../../utils/isolationReport';
import type { AppModule } from '../../../types';
import {
  nearestEdge,
  traceConnected,
  traceDisconnected,
  traceIsolation,
  type IsolationResult,
  type TraceEdge,
  type TraceResult,
} from '../../../utils/networkTrace';

/** One request's worth of records — ArcGIS commonly enforces its OWN
 *  `maxRecordCount` ceiling (often 1,000-2,000) regardless of what a single
 *  request's `resultRecordCount` asks for, so this is a PAGE size, not the
 *  real ceiling; `loadFeatures` below keeps asking for more pages until the
 *  server says there is nothing left. */
const PAGE_SIZE = 2000;

/**
 * A layer's features, from whichever service backs it — every page of them,
 * not just the first.
 *
 * A single-request fetch silently returning only the server's own first page
 * is exactly how a start flag dropped squarely on a real pipe produced "No
 * pipe under those start flags": the line IS there and IS rendered (the map
 * draws it through its own, unrelated tile/vector service), but a citywide
 * network of several thousand segments never fit in one capped query, so the
 * one page that happened to come back could easily hold none of the
 * segments near wherever this trace started from — a flag placed exactly on
 * a real pipe still found nothing, because that pipe's data was never
 * fetched at all. `limit` is a safety ceiling for a pathological service, not
 * the figure a normal trace is expected to hit.
 */
async function loadFeatures(layer: any, ctx: ToolContext, limit = 20000): Promise<GeoJSON.Feature[]> {
  if (layer.localData) return (layer.localData.features ?? []).slice(0, limit);

  if (layer.arcgisLayerId !== undefined) {
    const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return [];
    const features: GeoJSON.Feature[] = [];
    let offset = 0;
    while (features.length < limit) {
      const params = new URLSearchParams({
        where: layer.filter?.trim() || '1=1',
        outFields: '*',
        outSR: '4326',
        f: 'geojson',
        resultRecordCount: String(PAGE_SIZE),
        resultOffset: String(offset),
      });
      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
      );
      if (!response.ok) break;
      const data = await response.json();
      const page: GeoJSON.Feature[] = data?.features ?? [];
      features.push(...page);
      // The server says outright when it clipped the page — some services
      // enforce a ceiling below PAGE_SIZE, in which case a full-looking page
      // (page.length === their own cap) still means there is more to ask for.
      const exceeded =
        data?.exceededTransferLimit === true || data?.properties?.exceededTransferLimit === true;
      if (page.length === 0 || (!exceeded && page.length < PAGE_SIZE)) break;
      offset += page.length;
    }
    return features.slice(0, limit);
  }

  if (!ctx.geoServerUrl) return [];
  try {
    const url = new URL(ctx.geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('typeNames', layer.name);
    url.searchParams.set('outputFormat', 'application/json');
    // Without this, a layer whose STORED (native) CRS is not WGS84 comes back
    // in that native CRS, which this tool's isLine/isPoint connectivity walk
    // assumes is EPSG:4326 lon/lat — see featureSearchService.ts, the one
    // other WFS GetFeature caller that already set this.
    url.searchParams.set('srsName', 'EPSG:4326');
    if (layer.filter?.trim()) url.searchParams.set('cql_filter', layer.filter.trim());
    const features: GeoJSON.Feature[] = [];
    let startIndex = 0;
    while (features.length < limit) {
      url.searchParams.set('count', String(PAGE_SIZE));
      url.searchParams.set('startIndex', String(startIndex));
      const response = await fetch(url.toString());
      if (!response.ok) break;
      const data = await response.json();
      const page: GeoJSON.Feature[] = data?.features ?? [];
      features.push(...page);
      if (page.length < PAGE_SIZE) break;
      startIndex += page.length;
    }
    return features.slice(0, limit);
  } catch {
    return [];
  }
}

const isLine = (geometry: GeoJSON.Geometry | undefined) =>
  geometry?.type === 'LineString' || geometry?.type === 'MultiLineString';
const isPoint = (geometry: GeoJSON.Geometry | undefined) =>
  geometry?.type === 'Point' || geometry?.type === 'MultiPoint';

function formatLength(metres: number): string {
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(2)} km`;
}

/** A flag on the map: where a trace begins, or where it must stop. */
interface TracePoint {
  id: number;
  kind: 'start' | 'barrier';
  lat: number;
  lng: number;
}

export function TracePanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const configuredTolerance = cfg<number>(ctx.config, 'tolerance', 1);
  const maxEdges = cfg<number>(ctx.config, 'maxFeatures', 2000);
  const includeAssets = cfg<boolean>(ctx.config, 'includeAssets', true);

  const [networkIds, setNetworkIds] = useState<string[]>([]);
  const [tolerance, setTolerance] = useState(String(configuredTolerance));
  /** What the next map click places, or null when clicks are not wanted. */
  const [placing, setPlacing] = useState<'start' | 'barrier' | null>(null);
  const [points, setPoints] = useState<TracePoint[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TraceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const consumedRef = useRef('');

  /**
   * Connected (the original trace: walk outward, stop only at a manually
   * placed barrier), Disconnected (Esri's own Utility Network trace type,
   * the exact opposite — everything in the chosen layers the walk did NOT
   * reach; see traceDisconnected's own doc), or Valve Isolation (also
   * Esri's own, approximated geometrically — see traceIsolation's own doc
   * comment on the one honest translation of "which valves are shut" a
   * plain feature service can offer).
   */
  const [mode, setMode] = useState<'connected' | 'disconnected' | 'isolation'>('connected');
  /** Which point layer's features count as valves, in Isolation mode. */
  const [valveLayerId, setValveLayerId] = useState('');
  const [isolationResult, setIsolationResult] = useState<IsolationResult | null>(null);

  /**
   * Narrows WHICH of the valve layer's features actually count as a valve —
   * a real valve layer usually holds several device types (gate valves,
   * check valves, meter valves...) and not every one of them is something
   * an operator would physically close to isolate an area. Left blank,
   * every feature in the layer counts, same as before this existed.
   *
   * The value itself is both pickable AND typeable — a `<datalist>` offers
   * every distinct value the field actually holds (so someone browsing the
   * data can just choose one), but nothing requires picking from it: a code
   * an operator already has memorised can still be typed directly without
   * waiting for or scrolling through the list.
   */
  const [valveFieldNames, setValveFieldNames] = useState<string[]>([]);
  const [valveTypeField, setValveTypeField] = useState('');
  const [valveTypeValues, setValveTypeValues] = useState<string[]>([]);
  const [valveTypeInput, setValveTypeInput] = useState('');
  const [valveFieldValueOptions, setValveFieldValueOptions] = useState<string[]>([]);
  const [loadingValveFieldValues, setLoadingValveFieldValues] = useState(false);

  const addValveTypeValue = () => {
    const value = valveTypeInput.trim();
    if (!value) return;
    setValveTypeValues((current) => (current.includes(value) ? current : [...current, value]));
    setValveTypeInput('');
  };

  /* The valve layer's own field names, fetched the moment it is chosen —
     same "ask live on selection" pattern SearchPanel's field picker uses,
     rather than something the Tool Library would have to know about ahead
     of time for every possible valve layer an admin might ever pick. */
  useEffect(() => {
    setValveTypeField('');
    setValveTypeValues([]);
    if (mode !== 'isolation' || !valveLayerId) {
      setValveFieldNames([]);
      return;
    }
    const layer = ctx.layers.find((entry) => entry.id === valveLayerId);
    if (!layer) {
      setValveFieldNames([]);
      return;
    }
    let cancelled = false;
    const moduleContext = {
      ...ctx.module,
      geoServerUrl: ctx.geoServerUrl,
      arcgisUrl: ctx.arcgisUrl,
      layers: ctx.layers,
    } as AppModule;
    void fetchLayerFieldNames(layer, moduleContext).then((names) => {
      if (!cancelled) setValveFieldNames(names);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, valveLayerId]);

  /* The chosen field's own distinct values, for the datalist — read off the
     valve layer's already-loaded features rather than a separate per-source
     "distinct values" service call, the same simpler approach the Query
     tool's own field-value picker uses (loadFeatures already knows how to
     read local/ArcGIS/WFS alike). */
  useEffect(() => {
    setValveFieldValueOptions([]);
    if (mode !== 'isolation' || !valveLayerId || !valveTypeField) return;
    const layer = ctx.layers.find((entry) => entry.id === valveLayerId);
    if (!layer) return;
    let cancelled = false;
    setLoadingValveFieldValues(true);
    void loadFeatures(layer, ctx)
      .then((features) => {
        if (cancelled) return;
        const values = Array.from(
          new Set(
            features
              .map((feature) => feature.properties?.[valveTypeField])
              .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
              .map((value) => String(value).trim()),
          ),
        )
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          // A field with thousands of distinct values is a type-in box, not
          // a browsable list — the datalist stays useful either way, since
          // typing is always still possible.
          .slice(0, 200);
        setValveFieldValueOptions(values);
      })
      .finally(() => {
        if (!cancelled) setLoadingValveFieldValues(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, valveLayerId, valveTypeField]);

  /** Every layer the trace could use, split by what it can be used AS. */
  const candidates = useMemo(
    () => ({
      lines: allowed.filter((layer) => layer.geometryType === 'line' || layer.geometryType === undefined),
      points: allowed.filter((layer) => layer.geometryType === 'point'),
    }),
    [allowed],
  );

  const starts = points.filter((point) => point.kind === 'start');
  const barriers = points.filter((point) => point.kind === 'barrier');

  /* The flags on the map. Drawn from the SAME list the trace reads, so what is
     on screen and what the walk used can never disagree. */
  useEffect(() => {
    ctx.onOverlayChange?.(
      points.length === 0
        ? null
        : {
            type: 'FeatureCollection',
            features: points.map((point) => {
              const index =
                point.kind === 'start'
                  ? starts.findIndex((entry) => entry.id === point.id) + 1
                  : barriers.findIndex((entry) => entry.id === point.id) + 1;
              return {
                type: 'Feature' as const,
                properties: {
                  pin: point.kind,
                  marker: point.kind === 'start' ? `S${index}` : '✕',
                  label:
                    point.kind === 'start'
                      ? `Start ${index}`
                      : `Barrier ${index}`,
                },
                geometry: { type: 'Point' as const, coordinates: [point.lng, point.lat] },
              };
            }),
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  /* The flags belong to the tool, so they go when it does. */
  useEffect(
    () => () => ctx.onOverlayChange?.(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** A traced feature, onto the shared selection channel — the run (and in
   *  Isolation mode, the valves that bound it) highlighted in cyan on the
   *  map, with an open attribute table ticking the same rows. */
  const asSelectionRow = ({ layerId, feature }: { layerId: string; feature: GeoJSON.Feature }, index: number) => ({
    ...((feature.properties ?? {}) as Record<string, unknown>),
    id: String(feature.id ?? index),
    key: featureKey(feature, layerId),
    layerId,
    properties: (feature.properties ?? {}) as Record<string, unknown>,
    geometry: feature.geometry ?? undefined,
  });

  const runTrace = async () => {
    if (starts.length === 0) {
      setError('Place at least one start flag on a pipe.');
      return;
    }
    if (mode === 'isolation' && !valveLayerId) {
      setError('Choose which layer holds the valves.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const chosen = ctx.layers.filter((layer) => networkIds.includes(layer.id));

      // Lines make the network; points are what sits on it. A layer's geometry
      // decides which, so nobody has to declare it twice. In Isolation mode,
      // the chosen valve layer's points are pulled out separately — they are
      // the barriers, not ordinary assets riding on the run.
      const network: TraceEdge[] = [];
      const assets: { layerId: string; feature: GeoJSON.Feature }[] = [];
      const valves: { layerId: string; feature: GeoJSON.Feature }[] = [];

      const loaded = await Promise.all(
        chosen.map(async (layer) => ({ layer, features: await loadFeatures(layer, ctx) })),
      );
      // Normalised once, reused per feature below — matching case/whitespace
      // the same tolerant way the rest of this app's field comparisons do.
      const wantedValveTypes = new Set(valveTypeValues.map((value) => value.trim().toLowerCase()));

      for (const { layer, features } of loaded) {
        for (const feature of features) {
          if (isLine(feature.geometry)) {
            network.push({ layerId: layer.id, feature });
          } else if (isPoint(feature.geometry)) {
            if (mode === 'isolation' && layer.id === valveLayerId) {
              // No type field chosen (or no values typed under it) means
              // every feature in the layer counts, same as before this
              // existed. Otherwise only a feature whose own field value
              // matches one of the typed types is a valve — anything else
              // in that layer rides along as an ordinary asset instead.
              const isWantedType =
                !valveTypeField || wantedValveTypes.size === 0
                  ? true
                  : wantedValveTypes.has(
                      String(feature.properties?.[valveTypeField] ?? '').trim().toLowerCase(),
                    );
              if (isWantedType) {
                valves.push({ layerId: layer.id, feature });
              } else if (includeAssets) {
                assets.push({ layerId: layer.id, feature });
              }
            } else if (includeAssets) {
              assets.push({ layerId: layer.id, feature });
            }
          }
        }
      }

      if (network.length === 0) {
        setError('None of the chosen layers hold lines to trace along.');
        setResult(null);
        setIsolationResult(null);
        return;
      }

      const metres = Math.max(0.01, Number(tolerance) || 1);
      /* Ten tolerances, so a flag dropped slightly off a thin line still finds
         it — placing a flag is aiming, not surveying. */
      const snap = Math.max(metres * 10, 25);
      const startIndexes = starts
        .map((point) => nearestEdge(network, point, snap))
        .filter((index) => index >= 0);

      if (startIndexes.length === 0) {
        setError('No pipe under those start flags. Drop one directly on a line.');
        setResult(null);
        setIsolationResult(null);
        return;
      }

      if (mode === 'isolation') {
        if (valves.length === 0) {
          setError('That layer has no valve features to isolate against.');
          setIsolationResult(null);
          return;
        }
        const traced = traceIsolation(startIndexes, {
          network,
          valves,
          assets,
          toleranceMetres: metres,
          maxEdges,
        });
        setIsolationResult(traced);
        setResult(null);
        {
          const rows = [...traced.edges, ...traced.assets, ...traced.isolationValves].map(asSelectionRow);
          ctx.onSelectionChange?.(rows);
          ctx.onZoomToSelection?.(rows);
        }
      } else {
        const walk = mode === 'disconnected' ? traceDisconnected : traceConnected;
        const traced = walk(startIndexes, {
          network,
          assets,
          toleranceMetres: metres,
          maxEdges,
          barriers: barriers.map((point) => [point.lng, point.lat] as [number, number]),
        });
        setResult(traced);
        setIsolationResult(null);
        {
          const rows = [...traced.edges, ...traced.assets].map(asSelectionRow);
          ctx.onSelectionChange?.(rows);
          ctx.onZoomToSelection?.(rows);
        }
      }
    } catch {
      setError('The network could not be read from those layers.');
    } finally {
      setBusy(false);
    }
  };

  /* A map click drops the flag being placed, exactly where clicked — no
     visual nudge onto the line. Guarded on the coordinate, not a counter:
     `pointer` re-renders this panel for its own reasons, and a re-render is
     not a new click. Placing stays ON after each drop, because more than one
     flag is the point — of both kinds.
     The pipe match itself is `nearestEdge`'s job at Trace time (see
     `runTrace` below), with a tolerance far more generous than any flag
     placement needs — moving the flag's own drawn position was a separate,
     purely cosmetic correction that was tried and dropped; it is the matching
     tolerance below that actually decides which pipe a flag belongs to. */
  useEffect(() => {
    if (!placing || !ctx.pointer) return;
    const clicked = ctx.pointer;
    const signature = `${clicked.lat},${clicked.lng}`;
    if (signature === consumedRef.current) return;
    consumedRef.current = signature;
    setPoints((current) => [...current, { id: Date.now() + current.length, kind: placing, ...clicked }]);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placing, ctx.pointer]);

  const byLayer = useMemo(() => {
    if (!result) return [];
    const counts: Record<string, { lines: number; assets: number; metres: number }> = {};
    for (const edge of result.edges) {
      const bucket = (counts[edge.layerId] ||= { lines: 0, assets: 0, metres: 0 });
      bucket.lines += 1;
    }
    for (const asset of result.assets) {
      (counts[asset.layerId] ||= { lines: 0, assets: 0, metres: 0 }).assets += 1;
    }
    return ctx.layers
      .filter((layer) => counts[layer.id])
      .map((layer) => ({ layer, ...counts[layer.id] }));
  }, [result, ctx.layers]);

  /** The isolated/affected area, grouped by layer — "which layer, how much
   *  of it" is the result someone scanning an isolation plan reaches for
   *  first, same reasoning as Connected mode's own `byLayer` above. */
  const isolationByLayer = useMemo(() => {
    if (!isolationResult) return [];
    const counts: Record<string, { lines: number; assets: number; valves: number }> = {};
    for (const edge of isolationResult.edges) {
      (counts[edge.layerId] ||= { lines: 0, assets: 0, valves: 0 }).lines += 1;
    }
    for (const asset of isolationResult.assets) {
      (counts[asset.layerId] ||= { lines: 0, assets: 0, valves: 0 }).assets += 1;
    }
    for (const valve of isolationResult.isolationValves) {
      (counts[valve.layerId] ||= { lines: 0, assets: 0, valves: 0 }).valves += 1;
    }
    return ctx.layers
      .filter((layer) => counts[layer.id])
      .map((layer) => ({ layer, ...counts[layer.id] }));
  }, [isolationResult, ctx.layers]);

  /** The full isolation write-up — parameters, summary, valves to close, open
   *  ends — as the one PDF mechanism this app has: a canvas sheet handed to
   *  the browser's own print dialog, same as the Print tool. */
  const exportIsolationReport = () => {
    if (!isolationResult) return;
    const valveLayer = ctx.layers.find((entry) => entry.id === valveLayerId);
    const data: IsolationReportData = {
      title: 'Valve Isolation Trace Report',
      generatedAt: new Date(),
      startCount: starts.length,
      toleranceMetres: Number(tolerance) || 1,
      maxEdges,
      valveLayerName: valveLayer ? getLayerDisplayName(valveLayer) : null,
      valveTypeField: valveTypeField || null,
      valveTypeValues,
      isolatedLength: formatLength(isolationResult.totalMetres),
      segmentCount: isolationResult.edges.length,
      valveCount: isolationResult.isolationValves.length,
      assetCount: isolationResult.assets.length,
      openEndCount: isolationResult.openEnds.length,
      truncated: isolationResult.truncated,
      byLayer: isolationByLayer.map((group) => ({
        layerName: getLayerDisplayName(group.layer),
        lines: group.lines,
        assets: group.assets,
        valves: group.valves,
      })),
      valveRows: isolationResult.isolationValves.map(({ layerId, feature }, index) => {
        const layer = ctx.layers.find((entry) => entry.id === layerId);
        const label =
          Object.values(feature.properties ?? {}).find(
            (value) => typeof value === 'string' && value.trim() !== '',
          ) ?? `Valve ${index + 1}`;
        return { label: String(label), layerName: layer ? getLayerDisplayName(layer) : layerId };
      }),
      openEnds: isolationResult.openEnds,
    };
    printSheet(renderIsolationReportSheet(data), data.title);
  };

  /** Just the service points (every non-valve point asset the isolated run
   *  touched) with their full attributes — the sheet a crew in the field
   *  actually works off, separate from the PDF's narrative summary. */
  const exportServicePoints = () => {
    if (!isolationResult || isolationResult.assets.length === 0) return;
    const propertyKeys = Array.from(
      new Set(isolationResult.assets.flatMap((asset) => Object.keys(asset.feature.properties ?? {}))),
    );
    const columns = ['Layer', 'Latitude', 'Longitude', ...propertyKeys];
    const rows = isolationResult.assets.map((asset) => {
      const layer = ctx.layers.find((entry) => entry.id === asset.layerId);
      const position = asset.feature.geometry?.type === 'Point' ? asset.feature.geometry.coordinates : null;
      return {
        Layer: layer ? getLayerDisplayName(layer) : asset.layerId,
        Latitude: position ? position[1] : '',
        Longitude: position ? position[0] : '',
        ...(asset.feature.properties ?? {}),
      };
    });
    downloadCsv('isolation-service-points.csv', toCsv(columns, rows));
  };

  return (
    <>
      <ToolPanelHeader
        title={
          mode === 'isolation'
            ? 'Valve isolation trace'
            : mode === 'disconnected'
              ? 'Disconnected trace'
              : 'Connected trace'
        }
        description={
          mode === 'isolation'
            ? 'Flag the break, choose the valve layer, and find what to close.'
            : mode === 'disconnected'
              ? 'Flag a start, and find everything in the chosen layers that is NOT joined to it.'
              : 'Flag where to start, flag where to stop, then follow the network.'
        }
        onClose={ctx.onClose}
      />

      {/* Esri's Utility Network offers Disconnected and Isolation as their
          own trace types; same relationship here — one engine, a different
          question asked of it each time (see traceDisconnected's and
          traceIsolation's own docs on what changes). Switching modes clears
          whichever result belongs to the others, same as Clear does, so the
          panel never shows an answer to a question that is not the one
          currently being asked. */}
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => {
            setMode('connected');
            setResult(null);
            setIsolationResult(null);
          }}
          className={`rounded-md px-1.5 py-1 text-[11px] font-medium transition ${
            mode === 'connected' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
          }`}
        >
          Connected
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('disconnected');
            setResult(null);
            setIsolationResult(null);
          }}
          className={`rounded-md px-1.5 py-1 text-[11px] font-medium transition ${
            mode === 'disconnected' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
          }`}
        >
          Disconnected
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('isolation');
            setResult(null);
            setIsolationResult(null);
          }}
          className={`rounded-md px-1.5 py-1 text-[11px] font-medium transition ${
            mode === 'isolation' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
          }`}
        >
          Valve isolation
        </button>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-medium text-slate-600">Network layers</span>
          {allowed.length > 1 && (
            <button
              type="button"
              onClick={() =>
                setNetworkIds((current) =>
                  current.length > 0 ? [] : allowed.map((layer) => layer.id),
                )
              }
              className="text-[11px] font-semibold text-blue-600 transition hover:text-blue-800"
            >
              {networkIds.length > 0 ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-1.5">
          {allowed.length === 0 && (
            <p className="px-1 py-1 text-[11px] text-slate-400">No layers on this map yet.</p>
          )}
          {allowed.map((layer) => {
            const checked = networkIds.includes(layer.id);
            const isValveLayer = mode === 'isolation' && layer.id === valveLayerId;
            const role = isValveLayer ? 'valves' : candidates.points.includes(layer) ? 'assets' : 'lines';
            return (
              <label
                key={layer.id}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setNetworkIds((current) =>
                      checked ? current.filter((id) => id !== layer.id) : [...current, layer.id],
                    )
                  }
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                <span className="min-w-0 flex-1 truncate text-[12px] text-slate-700">
                  {getLayerDisplayName(layer)}
                </span>
                {/* Lines carry the trace, points ride on it — the layer's own
                    geometry decides which, so it is shown rather than asked. */}
                <span
                  className={`flex-shrink-0 text-[9.5px] uppercase tracking-wide ${
                    isValveLayer ? 'font-semibold text-red-500' : 'text-slate-400'
                  }`}
                >
                  {role}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {mode === 'isolation' && (
        <Field
          label="Valve layer"
          hint="Every feature in this layer counts as a closed valve — a geometric trace cannot know which ones really are, so the honest answer is every valve that bounds the area, for someone to act on. Narrow it to specific valve types below if the layer holds more than isolation valves."
        >
          <select
            value={valveLayerId}
            onChange={(event) => {
              const id = event.target.value;
              setValveLayerId(id);
              // Picking it here is the point of asking — it has to actually
              // be read, or the dropdown would be a promise this silently
              // did not keep.
              if (id) setNetworkIds((current) => (current.includes(id) ? current : [...current, id]));
            }}
            className={inputClass}
          >
            <option value="">— Choose a layer —</option>
            {candidates.points.map((layer) => (
              <option key={layer.id} value={layer.id}>
                {getLayerDisplayName(layer)}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* A real valve layer usually mixes device types (gate valves, check
          valves, meter valves…), and not every one of them is something an
          operator would physically close. This narrows WHICH field decides
          that — left on "every feature", nothing changes from before this
          existed. */}
      {mode === 'isolation' && valveLayerId && (
        <Field label="Valve type field" hint="Which field on that layer names the device type.">
          <select
            value={valveTypeField}
            onChange={(event) => {
              setValveTypeField(event.target.value);
              setValveTypeValues([]);
            }}
            className={inputClass}
          >
            <option value="">— Every feature is a valve —</option>
            {valveFieldNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* Free-type, not a fetched value list: the operator usually already
          knows the exact code ("GV", "Gate Valve") and typing it is faster
          than waiting on a service round trip — this only has to match an
          EXISTING field, which the dropdown above already guaranteed. Add as
          many as apply; a feature counts as a valve if it matches ANY one. */}
      {mode === 'isolation' && valveLayerId && valveTypeField && (
        <Field
          label="Valve type values"
          hint="Pick from the dropdown or type a value, then press Enter or Add. Add more than one if several types should isolate."
        >
          <div className="flex gap-1.5">
            <input
              value={valveTypeInput}
              onChange={(event) => setValveTypeInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addValveTypeValue();
              }}
              list="valve-type-value-options"
              placeholder={
                loadingValveFieldValues
                  ? 'Loading values…'
                  : valveFieldValueOptions.length > 0
                    ? `e.g. Gate Valve — ${valveFieldValueOptions.length} found`
                    : 'e.g. Gate Valve'
              }
              className={inputClass}
            />
            <button type="button" onClick={addValveTypeValue} className={`${btnClass} gl-btn--secondary`}>
              Add
            </button>
          </div>
          {/* The dropdown half of "pick OR type" — a native datalist offers
              every distinct value the field actually holds without
              preventing something not in that list from being typed. */}
          <datalist id="valve-type-value-options">
            {valveFieldValueOptions.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
          {valveTypeValues.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {valveTypeValues.map((value) => (
                <span
                  key={value}
                  className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                >
                  {value}
                  <button
                    type="button"
                    onClick={() => setValveTypeValues((current) => current.filter((entry) => entry !== value))}
                    aria-label={`Remove ${value}`}
                    className="text-slate-400 transition hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>
      )}

      <Field
        label="Join tolerance (m)"
        hint="How close two pipe ends must be to count as joined. Too small and a drawing with slight gaps falls apart; too large and separate mains fuse."
      >
        <input
          type="number"
          min={0.01}
          step={0.1}
          value={tolerance}
          onChange={(event) => setTolerance(event.target.value)}
          className={inputClass}
        />
      </Field>

      {/* Two flags, two meanings. A start says "begin here"; a barrier says
          "the network stops here" — which is the only way the CONNECTED
          trace can be told about a closed valve, since no plain feature
          service publishes device state. Isolation mode gets that from the
          valve layer instead, so there is nothing to place by hand — the
          start flag (the break point) is still placed the same way. */}
      <div className={mode === 'isolation' ? '' : 'grid grid-cols-2 gap-1.5'}>
        <button
          type="button"
          onClick={() => setPlacing((current) => (current === 'start' ? null : 'start'))}
          disabled={networkIds.length === 0}
          className={`${btnClass} ${placing === 'start' ? 'gl-btn--danger' : 'gl-btn--primary'} ${
            mode === 'isolation' ? 'w-full' : ''
          }`}
        >
          <MapPin className="h-3.5 w-3.5" />
          {placing === 'start' ? 'Stop placing' : mode === 'isolation' ? 'Add break point' : 'Add start'}
        </button>
        {mode !== 'isolation' && (
          <button
            type="button"
            onClick={() => setPlacing((current) => (current === 'barrier' ? null : 'barrier'))}
            disabled={networkIds.length === 0}
            className={`${btnClass} ${placing === 'barrier' ? 'gl-btn--danger' : 'gl-btn--secondary'}`}
          >
            <Ban className="h-3.5 w-3.5" />
            {placing === 'barrier' ? 'Stop placing' : 'Add barrier'}
          </button>
        )}
      </div>

      {placing && (
        <Notice>
          Click the map to drop {placing === 'start' ? 'a start flag' : 'a barrier'}. Keep clicking
          for more.
        </Notice>
      )}

      {points.length > 0 && (
        <div className="space-y-1">
          <ResultHeader label="Flags" count={points.length} />
          <ScrollList max={150}>
            {points.map((point) => {
              const index =
                point.kind === 'start'
                  ? starts.findIndex((entry) => entry.id === point.id) + 1
                  : barriers.findIndex((entry) => entry.id === point.id) + 1;
              return (
                <div
                  key={point.id}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5"
                >
                  <span
                    className={`flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded px-1 text-[9px] font-bold text-white ${
                      point.kind === 'start' ? 'bg-cyan-600' : 'bg-red-600'
                    }`}
                  >
                    {point.kind === 'start' ? `S${index}` : '✕'}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] tabular-nums text-slate-600">
                    {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      ctx.onGoToLocation([point.lat, point.lng], Math.max(ctx.mapZoom, 17))
                    }
                    className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-blue-600 transition hover:bg-blue-50"
                  >
                    Zoom
                  </button>
                  <button
                    type="button"
                    onClick={() => setPoints((current) => current.filter((p) => p.id !== point.id))}
                    className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-red-600 transition hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </ScrollList>
        </div>
      )}

      <ToolActions>
        <button
          type="button"
          onClick={() => void runTrace()}
          disabled={starts.length === 0 || busy}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : mode === 'isolation' ? (
            <ShieldAlert className="h-3.5 w-3.5" />
          ) : mode === 'disconnected' ? (
            <Unlink className="h-3.5 w-3.5" />
          ) : (
            <Share2 className="h-3.5 w-3.5" />
          )}
          {busy ? 'Tracing…' : `Trace${starts.length > 1 ? ` from ${starts.length}` : ''}`}
        </button>
        {(points.length > 0 || result || isolationResult) && (
          <button
            type="button"
            onClick={() => {
              setPoints([]);
              setPlacing(null);
              setResult(null);
              setIsolationResult(null);
              setError(null);
              ctx.onSelectionChange?.([]);
            }}
            className={`${btnClass} gl-btn--secondary`}
          >
            Clear
          </button>
        )}
      </ToolActions>

      {error && <Notice tone="warn">{error}</Notice>}

      {result && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {mode === 'disconnected' ? 'Disconnected length' : 'Network length'}
              </p>
              <p className="text-[14px] font-semibold tabular-nums text-slate-700">
                {formatLength(result.totalMetres)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Segments · assets
              </p>
              <p className="text-[14px] font-semibold tabular-nums text-slate-700">
                {result.edges.length} · {result.assets.length}
              </p>
            </div>
          </div>

          {result.blockedAt > 0 && (
            <Notice>
              {mode === 'disconnected'
                ? `${result.blockedAt} junction${result.blockedAt === 1 ? '' : 's'} stopped the connected side of this at your barriers — anything past one of those counts as disconnected here too.`
                : `Stopped at ${result.blockedAt} junction${result.blockedAt === 1 ? '' : 's'} because of your barriers. The pipe arriving at each one is included; what lies beyond is not.`}
            </Notice>
          )}

          {result.truncated && (
            <Notice tone="warn">
              {mode === 'disconnected'
                ? `The connected side of this was stopped at ${maxEdges.toLocaleString()} segments before it could finish — some of what's listed below may actually be connected, just not reached yet. Raise the limit in the Tool Library to be sure.`
                : `Stopped at ${maxEdges.toLocaleString()} segments. The run continues beyond this — raise the limit in the Tool Library, or check the drawing for ends that should not be joined.`}
            </Notice>
          )}

          {byLayer.length === 0 ? (
            <EmptyHint>
              {mode === 'disconnected'
                ? 'Nothing left over — every line in the chosen layers is connected to that start.'
                : 'Nothing was reached from there.'}
            </EmptyHint>
          ) : (
            <div className="space-y-1">
              <ResultHeader label={mode === 'disconnected' ? 'Not joined' : 'Reached'} count={byLayer.length} />
              <ScrollList max={200}>
                {byLayer.map((group) => (
                  <ResultRow
                    key={group.layer.id}
                    title={getLayerDisplayName(group.layer)}
                    detail={
                      group.lines > 0
                        ? `${group.lines} segment${group.lines === 1 ? '' : 's'}`
                        : `${group.assets} asset${group.assets === 1 ? '' : 's'}`
                    }
                    onClick={() => ctx.onOpenAttributeTable?.(group.layer)}
                    action={
                      <span className="flex-shrink-0 text-[11px] font-semibold tabular-nums text-slate-600">
                        {(group.lines || group.assets).toLocaleString()}
                      </span>
                    }
                  />
                ))}
              </ScrollList>
            </div>
          )}
        </div>
      )}

      {isolationResult && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Isolated length
              </p>
              <p className="text-[14px] font-semibold tabular-nums text-slate-700">
                {formatLength(isolationResult.totalMetres)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Segments · valves
              </p>
              <p className="text-[14px] font-semibold tabular-nums text-slate-700">
                {isolationResult.edges.length} · {isolationResult.isolationValves.length}
              </p>
            </div>
          </div>

          {/* The two things someone actually walks away with: a full written
              record of the plan (PDF, via the print dialog — no PDF library
              here, same as the Print tool), and just the service points, as
              a sheet a field crew can work off directly. */}
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={exportIsolationReport}
              className={`${btnClass} gl-btn--secondary`}
            >
              <FileText className="h-3.5 w-3.5" />
              PDF report
            </button>
            <button
              type="button"
              onClick={exportServicePoints}
              disabled={isolationResult.assets.length === 0}
              className={`${btnClass} gl-btn--secondary`}
              title={
                isolationResult.assets.length === 0
                  ? 'No service points on the isolated run'
                  : undefined
              }
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Service points (CSV)
            </button>
          </div>

          {/* The whole isolated/affected area, by layer — everything the
              trace reached between the break and the bounding valves,
              already on the shared selection channel above (cyan on the
              map, ticked in an open attribute table) and summarised here
              the same "which layer, how much" way Connected mode's own
              Reached list already does. */}
          {isolationByLayer.length > 0 && (
            <div className="space-y-1">
              <ResultHeader label="Isolated area, by layer" count={isolationByLayer.length} />
              <ScrollList max={200}>
                {isolationByLayer.map((group) => (
                  <ResultRow
                    key={group.layer.id}
                    title={getLayerDisplayName(group.layer)}
                    detail={[
                      group.lines > 0 ? `${group.lines} segment${group.lines === 1 ? '' : 's'}` : null,
                      group.assets > 0 ? `${group.assets} asset${group.assets === 1 ? '' : 's'}` : null,
                      group.valves > 0 ? `${group.valves} valve${group.valves === 1 ? '' : 's'}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    onClick={() => ctx.onOpenAttributeTable?.(group.layer)}
                    action={
                      <span className="flex-shrink-0 text-[11px] font-semibold tabular-nums text-slate-600">
                        {(group.lines + group.assets + group.valves).toLocaleString()}
                      </span>
                    }
                  />
                ))}
              </ScrollList>
            </div>
          )}

          {/* The whole point of the trace: not a count, the actual valves to
              act on. Esri's own Isolation Trace reports exactly this list. */}
          {isolationResult.isolationValves.length === 0 ? (
            <Notice tone="warn">
              No valve bounds this area — the trace ran off the edge of the network without being
              stopped anywhere. Nothing here can be isolated with the valves available.
            </Notice>
          ) : (
            <div className="space-y-1">
              <ResultHeader label="Close these valves" count={isolationResult.isolationValves.length} />
              <ScrollList max={200}>
                {isolationResult.isolationValves.map(({ layerId, feature }, index) => {
                  const position = feature.geometry?.type === 'Point' ? feature.geometry.coordinates : null;
                  const layer = ctx.layers.find((entry) => entry.id === layerId);
                  const label =
                    Object.values(feature.properties ?? {}).find(
                      (value) => typeof value === 'string' && value.trim() !== '',
                    ) ?? `Valve ${index + 1}`;
                  return (
                    <ResultRow
                      key={featureKey(feature, layerId) || index}
                      title={String(label)}
                      detail={layer ? getLayerDisplayName(layer) : layerId}
                      onClick={() => layer && ctx.onOpenAttributeTable?.(layer)}
                      action={
                        position ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              ctx.onGoToLocation([position[1], position[0]], Math.max(ctx.mapZoom, 18));
                            }}
                            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-blue-600 transition hover:bg-blue-50"
                          >
                            Zoom
                          </button>
                        ) : undefined
                      }
                    />
                  );
                })}
              </ScrollList>
            </div>
          )}

          {/* A leak in the isolation plan, named plainly — silently omitting
              this would be worse than not running the trace at all. */}
          {isolationResult.openEnds.length > 0 && (
            <Notice tone="warn">
              Not fully isolated: {isolationResult.openEnds.length} open end
              {isolationResult.openEnds.length === 1 ? '' : 's'} with no valve and nothing further
              connected. Flow can still escape there.
            </Notice>
          )}

          {isolationResult.truncated && (
            <Notice tone="warn">
              Stopped at {maxEdges.toLocaleString()} segments before finding every bounding valve —
              raise the limit in the Tool Library, or check the drawing for ends that should not be
              joined.
            </Notice>
          )}
        </div>
      )}

      {/* Said in the panel, not only in the source: someone reading a trace has
          to know what it could not see. */}
      <p className="text-[10.5px] leading-snug text-slate-400">
        {mode === 'isolation'
          ? 'This follows the drawing, not a utility-network model — a plain feature service publishes ' +
            'no device state, so every valve in the chosen layer is treated as closed, whether it ' +
            'really is right now or not. Treat the list above as a starting plan to verify in the ' +
            'field, not a guarantee.'
          : mode === 'disconnected'
            ? 'Everything in the chosen layers that the Connected walk from your start would NOT reach ' +
              '— a genuinely separate system, or two mains that look joined on the drawing but do not ' +
              'actually share an endpoint within tolerance. Worth checking either way.'
            : 'This follows the drawing, not a utility-network model — a plain feature service publishes ' +
              'neither flow direction nor device state. So the trace runs in every direction at once, and ' +
              'knows nothing about a shut valve until you put a barrier on it.'}
      </p>
    </>
  );
}
